/**
 * MobileTimelineSummary Component
 *
 * Container component for mobile view that displays weekly timeline data
 * as a vertical scrollable list of MobileWeekCard components.
 *
 * US-PE-006: Sales Timeline - Phase 4 Mobile View
 */

import MobileWeekCard from './MobileWeekCard'
import { formatWeekLabel, getWeekBounds } from '@/utils/timeline-utils'
import { createDate } from '@/utils/dateUtils'

/**
 * Get the week label for a week's data
 * @param {Array} weekData - Array of day objects with date property
 * @returns {string} Formatted week label
 */
function getWeekLabelFromData(weekData) {
  if (!weekData || weekData.length === 0) return 'Week'

  // Find the Monday (first day) of the week
  const firstDay = weekData[0]

  if (!firstDay?.date) return 'Week'

  // Parse date
  const startDate = typeof firstDay.date === 'string'
    ? createDate(firstDay.date)
    : firstDay.date

  // Get proper week bounds to ensure we have Monday-Sunday
  const { start, end } = getWeekBounds(startDate)

  return formatWeekLabel(start, end)
}

/**
 * MobileTimelineSummary - Container for mobile week cards
 *
 * @param {Object} props
 * @param {Array} props.weeklyData - Array of week arrays from useTimelineData
 *   Each week is an object with { weekStart, items } where items is an array of daily data
 * @param {Array} props.strains - Strain list for colour reference
 * @param {boolean} props.showAllStrains - Whether showing all strains
 * @param {Object} props.dateRange - Current date range { start, end }
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MobileTimelineSummary({
  weeklyData = [],
  strains = [],
  showAllStrains = true,
  dateRange = {}, // eslint-disable-line no-unused-vars -- Reserved for future date range header display
  className = ''
}) {
  // Handle empty state
  if (!weeklyData || weeklyData.length === 0) {
    return (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <p>No timeline data available for this period.</p>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {weeklyData.map((week, index) => {
        // Handle both formats: array of week arrays or array of { weekStart, items }
        const weekData = Array.isArray(week) ? week : week.items || []
        const weekLabel = Array.isArray(week)
          ? getWeekLabelFromData(weekData)
          : week.weekStart
            ? formatWeekLabel(week.weekStart, createDate(week.weekStart.getTime() + 6 * 24 * 60 * 60 * 1000))
            : getWeekLabelFromData(weekData)

        return (
          <MobileWeekCard
            key={index}
            weekData={weekData}
            weekLabel={weekLabel}
            strains={strains}
            showAllStrains={showAllStrains}
          />
        )
      })}
    </div>
  )
}
